import React from 'react'
import QuestionsFormComponent from './QuestionsFormComponent'

/**
 * Interactive Component Type Definition
 */
export interface InteractiveComponent {
  type: string
  id: string
  data: any
  answered: boolean
}

/**
 * Component Registry
 * Maps component types to their React components
 */
const COMPONENT_REGISTRY: Record<string, React.ComponentType<any>> = {
  'questions-form': QuestionsFormComponent,
  // Future components can be registered here:
  // 'code-editor': CodeEditorComponent,
  // 'chart-viewer': ChartViewerComponent,
  // 'file-upload': FileUploadComponent,
  // 'parameter-tuner': ParameterTunerComponent,
}

/**
 * Component Renderer Props
 */
interface ComponentRendererProps {
  component: InteractiveComponent
  onSubmit: (componentId: string, data: any) => void
}

/**
 * Component Renderer
 * Dynamically renders the appropriate component based on type
 */
export function ComponentRenderer({ component, onSubmit }: ComponentRendererProps) {
  console.log('ComponentRenderer rendering:', {
    type: component.type,
    id: component.id,
    answered: component.answered,
    hasData: !!component.data,
    data: component.data
  })

  const ComponentToRender = COMPONENT_REGISTRY[component.type]

  if (!ComponentToRender) {
    console.warn(`Unknown component type: ${component.type}`)
    return (
      <div 
        className="mt-4 p-4 rounded-lg" 
        style={{ 
          backgroundColor: 'var(--base-200)', 
          border: '1px solid var(--color-line)',
          color: 'var(--color-text-grey)'
        }}
      >
        <p>Unknown component type: {component.type}</p>
      </div>
    )
  }

  // If component has been answered, don't render it
  if (component.answered) {
    return null
  }

  return (
    <ComponentToRender
      data={component.data}
      onSubmit={(data: any) => onSubmit(component.id, data)}
    />
  )
}

/**
 * Helper function to format answers as natural language text
 * This is used when submitting form answers to create a user message
 */
export function formatAnswersAsText(
  componentType: string,
  componentData: any,
  answers: Record<string, any>
): string {
  if (componentType === 'questions-form') {
    const lines: string[] = []
    
    if (componentData.title) {
      lines.push(`**${componentData.title}**\n`)
    }
    
    componentData.questions?.forEach((question: any) => {
      const answer = answers[question.id]
      
      if (!answer || (Array.isArray(answer) && answer.length === 0) || (typeof answer === 'string' && answer.trim() === '')) {
        return // Skip unanswered questions
      }
      
      lines.push(`**${question.text}**`)
      
      if (question.type === 'single') {
        // Find the selected option label
        const selectedOption = question.options?.find((opt: any) => opt.id === answer)
        lines.push(`- ${selectedOption?.label || answer}`)
      } else if (question.type === 'multiple') {
        // Find all selected option labels
        const selectedOptions = question.options?.filter((opt: any) => answer.includes(opt.id))
        selectedOptions?.forEach((opt: any) => {
          lines.push(`- ${opt.label}`)
        })
      } else if (question.type === 'text') {
        lines.push(answer)
      }
      
      lines.push('') // Empty line between questions
    })
    
    return lines.join('\n').trim()
  }
  
  // Default fallback
  return JSON.stringify(answers, null, 2)
}

